import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';
import '../styles/login.css';
import { getBranding } from '../config/branding';
import { RequisitesFields, Requisites, EMPTY_REQUISITES } from './RequisitesFields';
import { Trans, useTranslation } from 'react-i18next';

interface Country {
  code: string;
  name: string;
  currency?: string;
}

type AccountType = 'self_service' | 'freelancer' | 'agency';

// `value` is the stored account_type — only the keys beside it are interface text.
const ACCOUNT_TYPES: { value: AccountType; labelKey: string; descKey: string; icon: React.ReactNode }[] = [
  {
    value: 'self_service',
    labelKey: 'auth.acctDirectClient',
    descKey: 'auth.acctDirectClientDesc',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    ),
  },
  {
    value: 'freelancer',
    labelKey: 'auth.acctFreelancer',
    descKey: 'auth.acctFreelancerDesc',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2L2 7l10 5 10-5-10-5z"/>
        <path d="M2 17l10 5 10-5"/>
        <path d="M2 12l10 5 10-5"/>
      </svg>
    ),
  },
  {
    value: 'agency',
    labelKey: 'auth.acctAgency',
    descKey: 'auth.acctAgencyDesc',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
  },
];

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [accountType, setAccountType] = useState<AccountType | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [requisites, setRequisites] = useState<Requisites>(EMPTY_REQUISITES);
  const [countries, setCountries] = useState<Country[]>([]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/auth/countries`)
      .then(res => res.json())
      .then(data => setCountries(data.countries || []))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!accountType) {
      setError(t('auth.errSelectType'));
      return;
    }

    if (password.length < 8) {
      setError(t('auth.errPasswordShort'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.errPasswordsMismatch'));
      return;
    }

    if ((accountType === 'agency' || accountType === 'self_service') && !companyName.trim()) {
      setError(t('auth.errCompanyRequired'));
      return;
    }

    if (!countryCode) {
      setError(t('auth.errSelectCountry'));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          first_name: firstName,
          last_name: lastName,
          company_name: companyName || undefined,
          phone: phone || undefined,
          country_code: countryCode,
          account_type: accountType,
          legal_name: requisites.legal_name || undefined,
          tax_id: requisites.tax_id || undefined,
          legal_address: requisites.legal_address || undefined,
          contract_number: requisites.contract_number || undefined,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
      } else {
        setError(data.detail || t('auth.errRegistration'));
      }
    } catch {
      setError(t('common.networkError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="login-page">
        <div className="login-bg">
          <div className="login-bg-gradient" />
          <div className="login-orb login-orb-1" />
          <div className="login-orb login-orb-2" />
          <div className="login-orb login-orb-3" />
        </div>
        <div className="login-container" style={{ maxWidth: 500 }}>
          <div className="login-form-container">
            <div className="login-form-card" style={{ maxWidth: 420 }}>
              <div className="login-form-header">
                <div style={{ fontSize: 48, marginBottom: 16 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#2ecc71" strokeWidth="2" style={{ width: 48, height: 48 }}>
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                </div>
                <h2>{t('auth.checkEmail')}</h2>
                <p style={{ marginTop: 12, lineHeight: 1.6 }}>
                  <Trans i18nKey="auth.verificationSent" values={{ email }} components={{ b: <strong /> }} />
                </p>
                <p style={{ marginTop: 16, fontSize: 13, color: '#999' }}>
                  {t('auth.adminReview')}
                </p>
              </div>
              <Link to="/login" className="login-submit" style={{ display: 'block', textAlign: 'center', textDecoration: 'none', marginTop: 24 }}>
                {t('auth.backToSignIn')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Edition-aware accent for inline-styled account-type cards.
  const isDievo = getBranding().edition !== 'sense';
  const accentColor = isDievo ? '#5E9E10' : '#FF6B4A';
  const accentBg = isDievo ? 'rgba(200,240,50,0.14)' : 'rgba(255, 107, 74, 0.05)';

  return (
    <div className="login-page">
      <div className="login-bg">
        <div className="login-bg-gradient" />
        <div className="login-orb login-orb-1" />
        <div className="login-orb login-orb-2" />
        <div className="login-orb login-orb-3" />
      </div>

      <div className="login-container" style={{ maxWidth: 1100 }}>
        {/* Left side - branding */}
        <div className="login-branding">
          <div className="login-branding-content">
            {getBranding().edition !== 'sense'
              ? <span className="brand-mark login-brand-mark">{getBranding().brand_name.toLowerCase()}</span>
              : <img src="/logo.png" alt={getBranding().brand_name} className="login-logo" />}
            <h1 className="login-tagline">
              {t('auth.taglineCreate')}<br />
              <span>{t('auth.taglineAccount')}</span>
            </h1>
            <p className="login-description">
              {t('auth.joinBrand', { brand: getBranding().brand_name })}
            </p>
          </div>
          <div className="login-branding-footer">
            <div className="login-feature">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              <span>{t('auth.featureSecurity')}</span>
            </div>
            <div className="login-feature">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
              <span>{t('auth.featureAnalytics')}</span>
            </div>
          </div>
        </div>

        {/* Right side - form */}
        <div className="login-form-container" style={{ overflowY: 'auto' }}>
          <div className="login-form-card" style={{ maxWidth: 420 }}>
            <div className="login-form-header">
              <h2>{t('auth.createAccount')}</h2>
            </div>

            {error && (
              <div className="login-error">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="login-form">
              {/* Account Type Selection */}
              <div className="login-field">
                <label style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{t('auth.iAmA')}</label>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: -8 }}>
                {ACCOUNT_TYPES.map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setAccountType(type.value)}
                    style={{
                      flex: 1,
                      padding: '12px 8px',
                      border: accountType === type.value
                        ? `2px solid ${accentColor}`
                        : '1px solid rgba(0,0,0,0.08)',
                      borderRadius: 10,
                      background: accountType === type.value
                        ? accentBg
                        : 'var(--bg-primary, #f8f9fa)',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s',
                    }}
                  >
                    <div style={{ width: 24, height: 24, margin: '0 auto 6px', color: accountType === type.value ? accentColor : '#adb5bd' }}>
                      {type.icon}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: accountType === type.value ? '#1a1a1a' : '#6c757d' }}>
                      {t(type.labelKey)}
                    </div>
                  </button>
                ))}
              </div>

              {/* Name fields */}
              <div style={{ display: 'flex', gap: 12 }}>
                <div className="login-field" style={{ flex: 1 }}>
                  <label htmlFor="firstName">{t('auth.firstName')}</label>
                  <div className="login-input-wrapper">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                    <input
                      id="firstName"
                      type="text"
                      value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                      placeholder={t('auth.firstNamePh')}
                      required
                    />
                  </div>
                </div>
                <div className="login-field" style={{ flex: 1 }}>
                  <label htmlFor="lastName">{t('auth.lastName')}</label>
                  <div className="login-input-wrapper">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                    <input
                      id="lastName"
                      type="text"
                      value={lastName}
                      onChange={e => setLastName(e.target.value)}
                      placeholder={t('auth.lastNamePh')}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="login-field">
                <label htmlFor="regEmail">{t('auth.email')}</label>
                <div className="login-input-wrapper">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  <input
                    id="regEmail"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="login-field">
                <label htmlFor="regPassword">{t('login.password')}</label>
                <div className="login-input-wrapper">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  <input
                    id="regPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder={t('auth.min8')}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="login-field">
                <label htmlFor="confirmPassword">{t('auth.confirmPassword')}</label>
                <div className="login-input-wrapper">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  <input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder={t('auth.repeatPassword')}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>
              </div>

              {/* Company Name */}
              <div className="login-field">
                <label htmlFor="companyName">
                  {t('auth.companyName')}{accountType === 'agency' || accountType === 'self_service' ? '' : t('auth.optionalSuffix')}
                </label>
                <div className="login-input-wrapper">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                  </svg>
                  <input
                    id="companyName"
                    type="text"
                    value={companyName}
                    onChange={e => setCompanyName(e.target.value)}
                    placeholder={t('auth.companyPh')}
                    required={accountType === 'agency' || accountType === 'self_service'}
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="login-field">
                <label htmlFor="phone">{t('auth.phoneOptional')}</label>
                <div className="login-input-wrapper">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                  <input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="+1 234 567 8900"
                  />
                </div>
              </div>

              {/* Country */}
              <div className="login-field">
                <label htmlFor="country">{t('auth.country')}</label>
                <div className="login-input-wrapper">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="2" y1="12" x2="22" y2="12"/>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                  </svg>
                  <select
                    id="country"
                    value={countryCode}
                    onChange={e => setCountryCode(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '14px 14px 14px 46px',
                      fontSize: 14,
                      color: countryCode ? 'var(--text-primary, #1a1a1a)' : 'var(--text-tertiary, #adb5bd)',
                      background: 'var(--bg-primary, #f8f9fa)',
                      border: '1px solid var(--border-color, rgba(0, 0, 0, 0.08))',
                      borderRadius: 10,
                      outline: 'none',
                      appearance: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="">{t('auth.selectCountry')}</option>
                    {countries.map(c => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {countries.find(c => c.code === countryCode)?.currency === 'KZT' && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #eee' }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{t('auth.legalDetails')}</div>
                  <div style={{ fontSize: 11, color: '#6c757d', marginBottom: 8 }}>
                    {t('auth.legalNote')}
                  </div>
                  <RequisitesFields value={requisites} onChange={setRequisites} />
                </div>
              )}

              <button
                type="submit"
                className="login-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="login-submit-spinner" />
                ) : (
                  t('auth.createAccount')
                )}
              </button>

              <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-secondary, #6c757d)', margin: '8px 0 0' }}>
                {t('auth.alreadyHaveAccount')}{' '}
                <Link to="/login" style={{ color: '#FF6B4A', fontWeight: 600, textDecoration: 'none' }}>
                  {t('login.signIn')}
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
